import React, { useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';

import { checkAcceptedFileTypes, getMessageByFundType, parseFullName } from 'utils/helpers';
import { texts } from 'utils/languages/fr';

import {
    indentedInputsSx,
    inputSx,
    labelSx,
    multilineSx,
    option2LabelSx,
    option3LabelSx,
    subLabelSx,
    textareaSx,
    uploadContainerSx,
    uploadDocSx,
} from './eligibilityStyles';
import { DocumentLine, DocumentUpload } from 'containers/tunnel/common';
import {
    CheckboxLabel,
    CustomSelect,
    CustomTextField,
    ErrorMessage,
    FileInputButton,
    RadioLabel,
} from 'components';
import { Box, RadioGroup, MenuItem, Typography } from '@mui/material';

const getInvestorQualificationQuestionNumber = investorQualification => {
    const match = investorQualification.match(/^(step34_fpci_pm_real\.question_)(\d+)/);
    return match ? parseInt(match[2]) : 0; // Return the number or 0 if no match
};

const CompanyEligibility = props => {
    const {
        handleRadioButtons,
        isSLP,
        messages,
        form,
        setForm,
        defaultForm,
        errors,
        isFCPR,
        isFPS,
        fundIsPro,
        handleChange,
        adminList,
        durations,
        sgpName,
        removeExemptionConditionsProof,
        handleExemptionConditionsProofChange,
    } = props;

    const {
        investorQualification,
        fixedCriteria,
        subscriptionRequestMotivation,
        internalContactUser,
        nameOfEntity,
        nameOfFIA,
        durationOfHolding,
        exemptionConditionsProof,
    } = form;

    const question16Data = useMemo(
        () => [
            {
                option: 1,
                label: texts.ELIGIBILITY_HAS_TOTAL_BALANCE_DOCS,
                docsFieldName: 'balanceSheetTotalDocuments',
                docs: form.balanceSheetTotalDocuments,
            },
            {
                option: 2,
                label: texts.ELIGIBILITY_HAS_TOTAL_BALANCE_DOCS,
                docsFieldName: 'netSalesOrRevenuesDocuments',
                docs: form.netSalesOrRevenuesDocuments,
            },
            {
                option: 3,
                label: texts.ELIGIBILITY_HAS_EQUITY_DOCS,
                docsFieldName: 'higherEquityDocuments',
                docs: form.higherEquityDocuments,
            },
        ],
        [form]
    );

    const handleFixedCriteriaChange = useCallback(
        e => {
            const name = e.target.name;
            const checked = e.target.checked;
            const optionNumber = +name.replace('step34_fpci_pm_real.question_16.option_', '');
            const optionDocsField = question16Data.find(
                q => q.option === optionNumber
            )?.docsFieldName;
            setForm(prevState => {
                const index = prevState.fixedCriteria.findIndex(el => el === name);
                if (index === -1 && checked) {
                    return {
                        ...prevState,
                        fixedCriteria: [...prevState.fixedCriteria, name],
                    };
                } else if (index >= 0 && !checked) {
                    return {
                        ...prevState,
                        fixedCriteria: [
                            ...prevState.fixedCriteria.slice(0, index),
                            ...prevState.fixedCriteria.slice(index + 1),
                        ],
                        [optionDocsField]: defaultForm[optionDocsField],
                    };
                }
                return prevState;
            });
        },
        [question16Data, defaultForm, setForm]
    );

    const removeFile = useCallback(
        (key, index) => () => {
            const findDoc = form[key].find((file, i) => i === +index && file.id);
            if (findDoc) {
                const docId = findDoc.id;
                setForm(prevState => ({
                    ...prevState,
                    [key]: prevState[key].filter(file => file.id !== docId || !file.id),
                }));
            } else {
                setForm(prevState => ({
                    ...prevState,
                    [key]: prevState[key].filter((file, i) => i !== index),
                }));
            }
        },
        [form, setForm]
    );

    const handleFileChange = useCallback(
        e => {
            const name = e.target.name;
            const file = e.target.files[0];
            checkAcceptedFileTypes(file, () => {
                setForm(prevState => ({ ...prevState, [name]: [...prevState[name], file] }));
            });
        },
        [setForm]
    );

    const investorTypeIsChecked = useMemo(() => {
        const questionNumber = getInvestorQualificationQuestionNumber(investorQualification);
        return questionNumber > 0 && questionNumber <= 16;
    }, [investorQualification]);

    return (
        <RadioGroup
            name="investorQualification"
            value={investorQualification}
            onChange={handleRadioButtons('investorQualification')}
        >
            {!isFCPR && (
                <React.Fragment>
                    {!fundIsPro && (
                        <React.Fragment>
                            <Box sx={multilineSx}>
                                <Box
                                    display="flex"
                                    alignItems="baseline"
                                    justifyContent="space-between"
                                >
                                    <RadioLabel
                                        label={messages['step34_fpci_pm_real.question_17']?.replace(
                                            '100 000',
                                            '100\xa0000'
                                        )}
                                        sx={labelSx}
                                        value="step34_fpci_pm_real.question_17"
                                    />
                                </Box>
                            </Box>
                            {!isSLP && (
                                <Box sx={multilineSx}>
                                    <Box
                                        display="flex"
                                        alignItems="baseline"
                                        justifyContent="space-between"
                                    >
                                        <RadioLabel
                                            label={
                                                <span>
                                                    {messages[
                                                        'step34_fpci_pm_real.question_18.label'
                                                    ]?.replace('30 000', '30\xa0000')}
                                                </span>
                                            }
                                            sx={labelSx}
                                            value="step34_fpci_pm_real.question_18.label"
                                            checked={
                                                investorQualification?.includes(
                                                    'step34.question_2'
                                                ) ||
                                                investorQualification?.includes(
                                                    'step34_fpci_pm_real.question_18'
                                                )
                                            }
                                        />
                                    </Box>
                                    {(investorQualification?.includes('step34.question_2') ||
                                        investorQualification?.includes(
                                            'step34_fpci_pm_real.question_18'
                                        )) && (
                                        <Box
                                            display="flex"
                                            flexDirection="column"
                                            alignItems="baseline"
                                            justifyContent="space-between"
                                            ml={7}
                                            mt={2}
                                        >
                                            <RadioGroup
                                                name="investorQualification"
                                                value={investorQualification}
                                                onChange={handleRadioButtons(
                                                    'investorQualification'
                                                )}
                                            >
                                                <RadioLabel
                                                    label={
                                                        messages[
                                                            'step34_fpci_pm_real.question_18.option_1.label'
                                                        ]
                                                    }
                                                    sx={subLabelSx}
                                                    value="step34_fpci_pm_real.question_18.option_1.label"
                                                />
                                                {investorQualification.includes('option_1') && (
                                                    <CustomTextField
                                                        multiline
                                                        rows={4}
                                                        formControlProps={{
                                                            sx: theme => ({
                                                                ...inputSx(theme),
                                                                ...indentedInputsSx,
                                                            }),
                                                        }}
                                                        sx={textareaSx}
                                                        label={texts.VOTRE_INTERVANTION}
                                                        value={subscriptionRequestMotivation}
                                                        onChange={handleChange(
                                                            'subscriptionRequestMotivation'
                                                        )}
                                                        error={errors.subscriptionRequestMotivation}
                                                    />
                                                )}
                                                <RadioLabel
                                                    label={
                                                        messages[
                                                            'step34_fpci_pm_real.question_18.option_2.label'
                                                        ]
                                                    }
                                                    sx={subLabelSx}
                                                    value="step34_fpci_pm_real.question_18.option_2.label"
                                                />
                                                {investorQualification.includes('option_2') && (
                                                    <CustomSelect
                                                        controlSx={theme => ({
                                                            ...inputSx(theme),
                                                            ...indentedInputsSx,
                                                            ...option2LabelSx,
                                                        })}
                                                        label={texts.INDIQUER_LE_NOM(sgpName)}
                                                        onChange={handleChange(
                                                            'internalContactUser'
                                                        )}
                                                        value={internalContactUser}
                                                    >
                                                        {adminList.map(admin => (
                                                            <MenuItem
                                                                key={admin.id}
                                                                value={admin.id}
                                                            >
                                                                {parseFullName(admin)}
                                                            </MenuItem>
                                                        ))}
                                                    </CustomSelect>
                                                )}
                                                <RadioLabel
                                                    label={
                                                        messages[
                                                            'step34_fpci_pm_real.question_18.option_3.label'
                                                        ]
                                                    }
                                                    sx={subLabelSx}
                                                    value="step34_fpci_pm_real.question_18.option_3.label"
                                                />
                                                {investorQualification.includes('option_3') && (
                                                    <>
                                                        <Typography
                                                            variant="body1"
                                                            color="textSecondary"
                                                            sx={option3LabelSx}
                                                        >
                                                            {texts.SEIN_DE_LAQUELL}
                                                        </Typography>
                                                        <CustomTextField
                                                            formControlProps={{
                                                                sx: theme => ({
                                                                    ...inputSx(theme),
                                                                    ...indentedInputsSx,
                                                                }),
                                                            }}
                                                            label={texts.NOM_DE_ENTITE}
                                                            value={nameOfEntity}
                                                            onChange={handleChange('nameOfEntity')}
                                                            error={errors.nameOfEntity}
                                                        />
                                                        <CustomTextField
                                                            formControlProps={{
                                                                sx: theme => ({
                                                                    ...inputSx(theme),
                                                                    ...indentedInputsSx,
                                                                }),
                                                            }}
                                                            label={texts.NOM_DU_FIA}
                                                            value={nameOfFIA}
                                                            onChange={handleChange('nameOfFIA')}
                                                            error={errors.nameOfFIA}
                                                        />
                                                        <CustomSelect
                                                            controlSx={theme => ({
                                                                ...inputSx(theme),
                                                                ...indentedInputsSx,
                                                            })}
                                                            label={texts.DUREE_DE_DETENTION}
                                                            onChange={handleChange(
                                                                'durationOfHolding'
                                                            )}
                                                            value={durationOfHolding}
                                                            error={errors.durationOfHolding}
                                                        >
                                                            {durations?.map(duration => (
                                                                <MenuItem
                                                                    key={duration}
                                                                    value={duration}
                                                                >
                                                                    {messages[duration]}
                                                                </MenuItem>
                                                            ))}
                                                        </CustomSelect>
                                                    </>
                                                )}
                                            </RadioGroup>
                                            {errors.investorQualification && (
                                                <ErrorMessage
                                                    sx={indentedInputsSx}
                                                    message={errors.investorQualification}
                                                />
                                            )}
                                        </Box>
                                    )}
                                    {(investorQualification?.includes('step34.question_2') ||
                                        investorQualification?.includes(
                                            'step34_fpci_pm_real.question_18'
                                        )) && (
                                        <DocumentUpload
                                            id="exemption-condition-proof-documents"
                                            isTableCell
                                            label={getMessageByFundType(
                                                messages,
                                                'step34.question_2.exemption_conditions_proof',
                                                isFPS
                                            )}
                                            selectedFiles={exemptionConditionsProof}
                                            handleFileChange={handleExemptionConditionsProofChange}
                                            removeFile={removeExemptionConditionsProof}
                                        />
                                    )}
                                    {errors.exemptionConditionsProof && (
                                        <ErrorMessage message={errors.exemptionConditionsProof} />
                                    )}
                                </Box>
                            )}
                        </React.Fragment>
                    )}
                </React.Fragment>
            )}

            <Box sx={multilineSx}>
                <Box>
                    <RadioLabel
                        checked={investorTypeIsChecked}
                        label={texts.ELIGIBILITY_INVESTOR_TYPE}
                        sx={subLabelSx}
                        value={'step34_fpci_pm_real.question_1'}
                    />
                    <CustomSelect
                        controlSx={inputSx}
                        onChange={handleRadioButtons('investorQualification')}
                        value={investorQualification}
                        disabled={!investorTypeIsChecked}
                    >
                        {Array.from({ length: 16 }, (v, k) => k + 1).map(index => (
                            <MenuItem
                                sx={theme => ({
                                    maxWidth: `${theme.constants.tunnelInputWidth}px`,
                                    textWrap: 'wrap',
                                })}
                                key={`step34_fpci_pm_real.question_${index}`}
                                value={
                                    index === 16
                                        ? `step34_fpci_pm_real.question_${index}.label`
                                        : `step34_fpci_pm_real.question_${index}`
                                }
                            >
                                {index === 16
                                    ? messages[`step34_fpci_pm_real.question_${index}.label`]
                                    : messages[`step34_fpci_pm_real.question_${index}`]}
                            </MenuItem>
                        ))}
                    </CustomSelect>
                    {investorQualification?.includes('question_16') && (
                        <Box
                            display="flex"
                            flexDirection="column"
                            alignItems="baseline"
                            justifyContent="space-between"
                            ml={7}
                            mt={2}
                        >
                            {question16Data.map(el => (
                                <React.Fragment key={el.option}>
                                    <CheckboxLabel
                                        label={
                                            messages[
                                                `step34_fpci_pm_real.question_16.option_${el.option}`
                                            ]
                                        }
                                        checked={fixedCriteria.includes(
                                            `step34_fpci_pm_real.question_16.option_${el.option}`
                                        )}
                                        onChange={handleFixedCriteriaChange}
                                        name={`step34_fpci_pm_real.question_16.option_${el.option}`}
                                    />
                                    {fixedCriteria.includes(
                                        `step34_fpci_pm_real.question_16.option_${el.option}`
                                    ) && (
                                        <Box sx={uploadContainerSx}>
                                            <Typography gutterBottom>{el.label}</Typography>
                                            <FileInputButton
                                                showIcon
                                                id={el.docsFieldName}
                                                label={texts.UPLOAD_FILE_LABEL}
                                                onFileChange={handleFileChange}
                                                noMargin
                                            />
                                            <Box sx={uploadDocSx}>
                                                {el.docs?.map((doc, index) => (
                                                    <DocumentLine
                                                        key={doc.id}
                                                        doc={doc}
                                                        onDelete={removeFile(
                                                            el.docsFieldName,
                                                            index
                                                        )}
                                                    />
                                                ))}
                                            </Box>
                                        </Box>
                                    )}
                                </React.Fragment>
                            ))}
                            {errors.fixedCriteria && (
                                <ErrorMessage
                                    sx={indentedInputsSx}
                                    message={errors.fixedCriteria}
                                />
                            )}
                            <Typography variant="caption" component="i" gutterBottom>
                                {texts.OPTIONAL_DOCS_EXPLANATION}
                            </Typography>
                        </Box>
                    )}
                </Box>
            </Box>

            {!isFCPR && (
                <React.Fragment>
                    <Box sx={multilineSx}>
                        <Box display="flex" alignItems="baseline" justifyContent="space-between">
                            <RadioLabel
                                label={messages['step34_fpci_pm_real.question_19']}
                                sx={labelSx}
                                value="step34_fpci_pm_real.question_19"
                            />
                        </Box>
                    </Box>
                    <Box sx={multilineSx}>
                        <Box display="flex" alignItems="baseline" justifyContent="space-between">
                            <RadioLabel
                                label={getMessageByFundType(
                                    messages,
                                    'step34_fpci_pm_real.question_20',
                                    isFPS
                                )}
                                sx={labelSx}
                                value="step34_fpci_pm_real.question_20"
                            />
                        </Box>
                    </Box>
                </React.Fragment>
            )}
        </RadioGroup>
    );
};

CompanyEligibility.propTypes = {
    handleRadioButtons: PropTypes.func,
    isSLP: PropTypes.bool,
    messages: PropTypes.object,
    form: PropTypes.object,
    setForm: PropTypes.func,
    defaultForm: PropTypes.object,
    errors: PropTypes.object,
    isFCPR: PropTypes.bool,
    isFPS: PropTypes.bool,
    fundIsPro: PropTypes.bool,
    handleChange: PropTypes.func,
    adminList: PropTypes.array,
    durations: PropTypes.array,
    sgpName: PropTypes.string,
    removeExemptionConditionsProof: PropTypes.func,
    handleExemptionConditionsProofChange: PropTypes.func,
};
export default CompanyEligibility;
